import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Users, AlertTriangle, MapPin, Star,
  Lightbulb, Zap, CheckCircle2
} from 'lucide-react';
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface ClientPortfolioModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
}

const ClientPortfolioModal: React.FC<ClientPortfolioModalProps> = ({ isOpen, onClose, data }) => {
  const [selectedTierIndex, setSelectedTierIndex] = useState<number | undefined>(undefined);

  const formatNumber = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toFixed(0);
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1)}k`;
    }
    return `R$ ${value.toFixed(2)}`;
  };

  const getTierColor = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case 'gold':
        return { color: '#FF006E', darkColor: '#C2185B', emoji: '👑' };
      case 'silver':
        return { color: '#00D9FF', darkColor: '#0097A7', emoji: '🥈' };
      case 'bronze':
        return { color: '#FFD700', darkColor: '#FFA500', emoji: '🥉' };
      default:
        return { color: '#F3F4F6', darkColor: '#D1D5DB', emoji: '📊' };
    }
  };

  // Extrair dados
  const analysis = data?.analysis || {};
  const segmentation = data?.segmentation || {};
  const riskAnalysis = data?.risk_analysis || {};
  const portfolioHealth = data?.portfolio_health || {};
  const geographicData = analysis?.geographic_analysis || {};
  const strategicInsights = analysis?.strategic_insights || [];
  const recommendations = analysis?.recommendations || [];

  // Preparar dados para gráficos
  const tierSegmentData = segmentation?.by_tier ?
    Object.entries(segmentation.by_tier).map(([tier, count]: any) => ({
      name: tier,
      value: count as number
    })) : [];

  const topStatesData = geographicData?.by_state ?
    Object.entries(geographicData.by_state)
      .sort(([,a]: any, [,b]: any) => (b as number) - (a as number))
      .slice(0, 8)
      .map(([state, count]: any) => ({
        name: state,
        clientes: count as number
      })) : [];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <style>{`
              .portfolio-modal * {
                outline: none !important;
                -webkit-focus-ring-color: transparent !important;
              }
              .portfolio-modal *:focus {
                outline: none !important;
                box-shadow: none !important;
              }
            `}</style>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="border-2 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden portfolio-modal"
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
            {/* Header */}
            <div
              className="px-6 py-5 border-b"
              style={{ borderColor: 'var(--border-primary)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: 'var(--bg-tertiary)' }}
                  >
                    <Users className="h-6 w-6" style={{ color: '#ef4444' }} />
                  </div>
                  <div>
                    <h2
                      className="text-3xl font-bold"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Carteira de Clientes
                    </h2>
                    <p
                      className="text-sm mt-1"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      Segmentação, Risco e Oportunidades
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--bg-tertiary)' }}
                >
                  <X className="h-6 w-6" style={{ color: 'var(--text-secondary)' }} />
                </motion.button>
              </div>
            </div>

            {/* Content */}
            <div className="px-8 py-8 overflow-y-auto max-h-[calc(90vh-120px)] space-y-8">

              {/* Explicação de Metodologia */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-5 rounded-lg border-l-4"
                style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.05)',
                  borderColor: '#3B82F6'
                }}
              >
                <p className="text-sm font-bold mb-3" style={{ color: '#3B82F6' }}>
                  📊 Como os dados foram calculados:
                </p>
                <ul className="text-xs space-y-2" style={{ color: 'var(--text-secondary)' }}>
                  <li><strong style={{ color: 'var(--text-primary)' }}>Tiers (Gold/Silver/Bronze):</strong> Clientes agrupados por valor: limite de crédito, classificação (Apex, Prime, Core, Base) e localização geográfica</li>
                  <li><strong style={{ color: 'var(--text-primary)' }}>Risco & Oportunidades:</strong> Calculado a partir do status de atividade (ATIVO/INATIVO) + potencial de crescimento (ALTO/MEDIO/BAIXO)</li>
                  <li><strong style={{ color: 'var(--text-primary)' }}>Geográfico:</strong> Distribuição real dos clientes por estado, cidade e regional baseada em dados cadastrais</li>
                </ul>
              </motion.div>

              {/* KPIs - Métricas Principais */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 md:grid-cols-4 gap-4"
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-6 rounded-xl text-center"
                  style={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',
                    boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <div className="text-4xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    {portfolioHealth.total_clients || 0}
                  </div>
                  <div className="text-xs font-bold text-white mb-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Total de Clientes</div>
                  <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                    {geographicData.total_states || 0} estados
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="p-6 rounded-xl text-center"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  <div className="text-4xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    {geographicData.total_cities || 0}
                  </div>
                  <div className="text-xs font-bold text-white mb-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Cidades Cobertas</div>
                  <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                    {geographicData.total_regionals || 0} regionais
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="p-6 rounded-xl text-center"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: '0 4px 6px rgba(245, 158, 11, 0.3)'
                  }}
                >
                  <div className="text-3xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    {formatCurrency(portfolioHealth.average_ticket || 0)}
                  </div>
                  <div className="text-xs font-bold text-white mb-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Ticket Médio</div>
                  <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                    Valor médio por cliente
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="p-6 rounded-xl text-center"
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                    boxShadow: '0 4px 6px rgba(139, 92, 246, 0.3)'
                  }}
                >
                  <div className="text-4xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    {tierSegmentData.length}
                  </div>
                  <div className="text-xs font-bold text-white mb-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Tiers de Clientes</div>
                  <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                    Segmentação ativa
                  </p>
                </motion.div>
              </motion.div>

              {/* Segmentação por Tier - Gráfico Pizza */}
              {tierSegmentData.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-0"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none'
                  }}
                >
                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <Star className="w-6 h-6" style={{ color: '#FF006E', fill: '#FF006E' }} />
                      Segmentação de Clientes por Tier (Powered by AI)
                    </h3>
                    <div className="space-y-3">
                      <p className="text-xs p-3 rounded bg-blue-50" style={{ color: '#333', borderLeft: '3px solid #3B82F6' }}>
                        <strong>🤖 Metodologia AI:</strong> Segmentação unificada e inteligente baseada em 4 critérios ponderados:
                        <ul className="mt-2 ml-4 space-y-1" style={{ listStyleType: 'disc' }}>
                          <li><strong>40% - Limite de Crédito:</strong> Capacidade financeira do cliente</li>
                          <li><strong>30% - Classificação:</strong> Apex (Topo), Prime, Core, Base</li>
                          <li><strong>20% - Atividade:</strong> Status ATIVO vs INATIVO</li>
                          <li><strong>10% - Crescimento:</strong> Potencial ALTO, MÉDIO, BAIXO</li>
                        </ul>
                      </p>
                      <p className="text-xs p-3 rounded bg-purple-50" style={{ color: '#333', borderLeft: '3px solid #9333EA' }}>
                        <strong>📊 Distribuição:</strong> Gold (Top 20%), Silver (30%), Bronze (50%) - otimizado para balanceamento e oportunidades de crescimento.
                      </p>
                    </div>
                    <p className="text-xs mt-3 text-gray-500">Clique em um tier para expandir a visualização no gráfico.</p>
                  </div>
                  <div className="space-y-8">
                    <div style={{ outline: 'none', WebkitFocusRingsColor: 'transparent' }}>
                    <ResponsiveContainer width="100%" height={350} style={{ outline: 'none' } as any}>
                      <PieChart>
                        <Pie
                          data={tierSegmentData}
                          cx="50%"
                          cy="50%"
                          outerRadius={selectedTierIndex !== undefined ? 140 : 90}
                          fill="#10b981"
                          dataKey="value"
                          activeIndex={selectedTierIndex}
                          activeShape={{
                            filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.6))',
                            stroke: '#fff',
                            strokeWidth: 3,
                          } as any}
                        >
                          {tierSegmentData.map((_: any, index: number) => {
                            const colors = ['#FF006E', '#00D9FF', '#FFD700'];
                            const isSelected = selectedTierIndex === index;
                            return (
                              <Cell
                                key={`cell-${index}`}
                                fill={colors[index % colors.length]}
                                opacity={isSelected ? 1 : 0.9}
                                stroke={isSelected ? '#fff' : 'rgba(255,255,255,0.5)'}
                                strokeWidth={isSelected ? 4 : 2}
                              />
                            );
                          })}
                        </Pie>
                        <Tooltip
                          formatter={(value: any) => formatNumber(value)}
                          contentStyle={{
                            backgroundColor: 'var(--bg-secondary)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '8px',
                            color: 'var(--text-primary)'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {tierSegmentData.map((tier: any, idx: number) => {
                        const isSelected = selectedTierIndex === idx;
                        const tierColor = getTierColor(tier.name);
                        const tierKey = `${tier.name.toLowerCase()}_details` as keyof typeof data.segmentation;
                        const tierDetails = data?.segmentation?.[tierKey];

                        return (
                          <motion.div
                            key={idx}
                            onClick={() => setSelectedTierIndex(isSelected ? undefined : idx)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="p-4 rounded-lg cursor-pointer transition-all shadow-md hover:shadow-lg"
                            style={{
                              backgroundColor: isSelected ? `${tierColor.color}25` : `${tierColor.color}10`,
                              border: `3px solid ${tierColor.color}`,
                              boxShadow: isSelected ? `0 0 20px ${tierColor.color}40` : 'none'
                            }}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">{tierColor.emoji}</span>
                                <span className="font-bold" style={{ color: 'var(--text-primary)' }}>
                                  {tier.name}
                                </span>
                              </div>
                              <span className="font-bold" style={{ color: tierColor.darkColor }}>
                                {tier.value} clientes
                              </span>
                            </div>
                            {tierDetails && (
                              <div className="text-xs space-y-2" style={{ color: 'var(--text-secondary)' }}>
                                <div>Crédito Médio: <span style={{ color: tierColor.darkColor }}>R$ {((tierDetails?.avg_credit_limit || 0) / 1000).toFixed(1)}k</span></div>
                                <div>Taxa Ativa: <span style={{ color: tierColor.darkColor }}>{(tierDetails?.active_percentage || 0).toFixed(1)}%</span></div>
                                <div>Alto Potencial: <span style={{ color: tierColor.darkColor }}>{tierDetails?.high_potential || 0} clientes</span></div>
                                {/* AI Insights */}
                                {tierDetails?.ai_insights && (
                                  <div className="mt-2 pt-2 border-t border-gray-300" style={{ borderColor: `${tierColor.color}40` }}>
                                    <p className="text-xs font-semibold" style={{ color: tierColor.color }}>💡 Insights IA:</p>
                                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                                      {tierDetails.ai_insights}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Distribuição Geográfica - Gráfico Barras */}
              {topStatesData.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 rounded-2xl shadow-lg"
                  style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                >
                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      <MapPin className="w-6 h-6" style={{ color: '#3B82F6' }} />
                      Distribuição Geográfica
                    </h3>
                    <p className="text-xs p-3 rounded bg-blue-50" style={{ color: '#666', borderLeft: '3px solid #3B82F6' }}>
                      <strong>📍 Explicação:</strong> Este gráfico mostra onde seus clientes estão localizados. A altura de cada barra representa quantos clientes você tem em cada estado.
                      Total: <strong>{geographicData.total_states || 0} estados</strong> com <strong>{geographicData.total_cities || 0} cidades</strong> cobertas. Quanto mais distribuído, melhor o risco.
                    </p>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={topStatesData}
                      margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--border-primary)"
                      />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                      />
                      <YAxis
                        tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: any) => formatNumber(value)}
                        contentStyle={{
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--border-primary)',
                          borderRadius: '8px',
                          color: 'var(--text-primary)'
                        }}
                      />
                      <Bar
                        dataKey="clientes"
                        fill="#3B82F6"
                        radius={[8, 8, 0, 0]}
                        name="Clientes"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </motion.div>
              )}

              {/* Risco & Oportunidade */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="mb-4">
                  <h3 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <AlertTriangle className="w-6 h-6" style={{ color: '#f59e0b' }} />
                    Risco & Oportunidade
                  </h3>
                  <div className="text-xs p-4 rounded" style={{ backgroundColor: 'rgba(245, 158, 11, 0.05)', borderLeft: '3px solid #f59e0b', color: '#666' }}>
                    <p className="mb-2"><strong style={{ color: 'var(--text-primary)' }}>💡 O que significa cada categoria:</strong></p>
                    <ul className="space-y-2 ml-2">
                      <li><strong>🔴 Em Risco:</strong> Clientes que <strong>pararam de comprar (INATIVOS)</strong>. O "Valor em Risco" mostra o limite de crédito total desses clientes que não estão sendo utilizado - é receita que você está perdendo.</li>
                      <li><strong>🟢 Reativação:</strong> Clientes que <strong>pararam de comprar mas têm potencial</strong>. O "Potencial" mostra quanto de receita você poderia recuperar se conseguir reativá-los. É geralmente o melhor retorno para investimento.</li>
                      <li><strong>🔵 Crescimento:</strong> Seus <strong>melhores clientes</strong> - já estão comprando e têm potencial para comprar mais. O "Potencial" mostra quanto mais você pode expandir com eles.</li>
                    </ul>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Em Risco */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="rounded-xl p-5 border-2"
                    style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.05)',
                      borderColor: '#EF4444'
                    }}
                  >
                    <p className="text-sm font-bold mb-3" style={{ color: '#EF4444' }}>🔴 Em Risco</p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Clientes</p>
                        <p className="text-2xl font-bold" style={{ color: '#EF4444' }}>
                          {riskAnalysis.at_risk?.count || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>% da Base</p>
                        <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                          {((riskAnalysis.at_risk?.percentage || 0).toFixed(1))}%
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Reativação */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="rounded-xl p-5 border-2"
                    style={{
                      backgroundColor: 'rgba(34, 197, 94, 0.05)',
                      borderColor: '#22C55E'
                    }}
                  >
                    <p className="text-sm font-bold mb-3" style={{ color: '#22C55E' }}>🟢 Reativação</p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Clientes</p>
                        <p className="text-2xl font-bold" style={{ color: '#22C55E' }}>
                          {riskAnalysis.reactivation_opportunity?.count || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Potencial</p>
                        <p className="text-sm font-bold" style={{ color: '#22C55E' }}>
                          {formatCurrency(riskAnalysis.reactivation_opportunity?.potential_value || 0)}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Crescimento */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="rounded-xl p-5 border-2"
                    style={{
                      backgroundColor: 'rgba(59, 130, 246, 0.05)',
                      borderColor: '#3B82F6'
                    }}
                  >
                    <p className="text-sm font-bold mb-3" style={{ color: '#3B82F6' }}>🔵 Crescimento</p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Clientes</p>
                        <p className="text-2xl font-bold" style={{ color: '#3B82F6' }}>
                          {riskAnalysis.growth_opportunity?.count || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Potencial</p>
                        <p className="text-sm font-bold" style={{ color: '#3B82F6' }}>
                          {formatCurrency(riskAnalysis.growth_opportunity?.potential_value || 0)}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Insights Estratégicos */}
              {strategicInsights.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Lightbulb className="w-6 h-6" style={{ color: '#f59e0b' }} />
                    Insights Estratégicos
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {strategicInsights.slice(0, 6).map((insight: any, idx: number) => {
                      const insightType = typeof insight === 'string' ? 'insight' : (insight.type || 'insight');
                      const insightText = typeof insight === 'string' ? insight : (insight.insight || insight.title || insight);
                      const isObject = typeof insight === 'object';
                      const insightTitle = isObject ? insight.title : null;

                      const typeConfig: any = {
                        'opportunity': { icon: Zap, color: '#10B981', label: '✨ Oportunidade' },
                        'warning': { icon: AlertTriangle, color: '#F59E0B', label: '⚠️ Atenção' },
                        'recommendation': { icon: CheckCircle2, color: '#3B82F6', label: '💡 Recomendação' },
                        'insight': { icon: Lightbulb, color: 'var(--primary)', label: '📊 Insight' }
                      };

                      const config = typeConfig[insightType] || typeConfig['insight'];
                      const Icon = config.icon;

                      return (
                        <div
                          key={idx}
                          className="relative rounded-lg overflow-hidden border-2 p-4"
                          style={{
                            backgroundColor: 'var(--bg-secondary)',
                            borderColor: config.color,
                            minHeight: '140px',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="w-4 h-4" style={{ color: config.color }} />
                            <span className="text-xs font-bold" style={{ color: config.color }}>
                              {config.label}
                            </span>
                          </div>
                          {insightTitle && (
                            <p className="text-xs font-bold mb-2 line-clamp-2" style={{ color: 'var(--text-primary)' }}>
                              {insightTitle}
                            </p>
                          )}
                          <p className="text-xs leading-relaxed flex-1" style={{ color: 'var(--text-secondary)' }}>
                            {insightText}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Recomendações */}
              {recommendations.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <CheckCircle2 className="w-6 h-6" style={{ color: '#10b981' }} />
                    Recomendações Acionáveis
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {recommendations.slice(0, 4).map((rec: any, idx: number) => {
                      const recText = typeof rec === 'string' ? rec : (rec.text || rec.description || rec.recommendation || rec);
                      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EC4899'];
                      const emoji = ['🎯', '📈', '⚡', '💎'][idx % 4];
                      const color = colors[idx % colors.length];

                      return (
                        <div
                          key={idx}
                          className="relative rounded-lg overflow-hidden border-2 p-4"
                          style={{
                            backgroundColor: 'var(--bg-secondary)',
                            borderColor: color,
                            minHeight: '120px',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <div className="text-2xl mb-2">{emoji}</div>
                          <p className="text-sm leading-relaxed flex-1 font-medium" style={{ color: 'var(--text-primary)' }}>
                            {recText}
                          </p>
                          <div className="mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                            <p className="text-xs font-semibold" style={{ color }}>→ Iniciar implementação</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ClientPortfolioModal;
